/**
 * @file HeroCard.tsx
 * @description Landscape hero card for featured content carousel
 *
 * Design specifications:
 * - Uses backdropUrlHd for landscape imagery
 * - Spans ~80% of viewport width
 * - 16:9 aspect ratio (landscape)
 * - Gradient overlay for text readability
 * - Metadata overlay: title, synopsis, metadata, CTAs
 * - Responsive design with mobile/tablet/desktop variants
 *
 * @architecture
 * Implements ARCH-001 featured content display strategy
 * Part of RAILS-02 Featured Hero Rail implementation
 */

import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import type { Variants } from "framer-motion";
import { Info, Play } from "lucide-react";
import { pageTransitions } from "../../transitions/pageTransitions";

interface HeroCardProps {
  readonly title: ApiTitle;
  readonly transitionTo: (path: string, variant?: Variants) => void;
  readonly className?: string;
}

/**
 * Formats runtime from minutes to "Xh Ym" display format
 */
function formatRuntime(minutes: number | undefined): string | null {
  if (!minutes) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
}

/**
 * Hero card component for featured content carousel
 *
 * Displays landscape imagery with overlaid metadata and CTAs
 * Optimized for large-screen hero display
 */

export function HeroCard({ title, transitionTo, className = "" }: HeroCardProps): ReactElement {
  const heroImage = title.backdropUrlHd || title.horizontalTileUrl || title.heroUrl;
  const runtime = formatRuntime(title.runtimeMinutes ?? undefined);

  const logoUrl = title.logoUrl ?? null;

  const handleMoreInfo = () => {
    // More Info uses fullScreenFade for immersive title details (global pointerdown listener exits nav mode)
    transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
  };

  const handlePlay = () => {
    // Play uses cinematicFade for dramatic video player entrance
    transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade);
  };

  return (
    <div className={`group flex-shrink-0 snap-center ${className}`} aria-label={`${title.name} - Play`}>
      <div className="shadow-[0_10px_40px_rgba(0,0,0,0.7)] rounded-lg">
        <div
          className="
                        card-base card-hover-hero
                        aspect-video
                        flex-shrink-0 
                        w-[90vw] sm:w-[92vw] md:w-[92vw] lg:w-[80vw] xl:w-[55vw]
                        border border-neutral-900 
                    "
        >
          {/* Background image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-lg"
            style={{ backgroundImage: `url(${heroImage})` }}
          />

          {/* Gradient overlays for text readability */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/20 to-transparent rounded" />
          <div className="card-gradient-light" />
          <div className="card-gradient-dark" />

          {/* Content overlay */}
          <div className="absolute inset-0 flex items-end p-8 md:p-12 lg:p-16 z-20">
            <div className="w-full grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-8 lg:gap-12 items-end">
              {/* Column 1: Title + Metadata */}
              <div className="space-y-2 md:space-y-3">
                {/* Title logo or text */}
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={title.name}
                    className="h-16 md:h-20 lg:h-24 w-auto object-contain object-left"
                  />
                ) : (
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-white drop-shadow-lg">
                    {title.name}
                  </h2>
                )}

                {/* Metadata badges */}
                <div className="flex items-center gap-2 text-xs md:text-sm flex-wrap">
                  {title.year && <span className="text-neutral-300 font-medium">{title.year}</span>}
                  {title.year && runtime && <span className="text-neutral-500">•</span>}
                  {runtime && <span className="text-neutral-300 font-medium">{runtime}</span>}
                  {(title.year || runtime) && title.genres && title.genres.length > 0 && (
                    <span className="text-neutral-500">•</span>
                  )}
                  {title.genres && title.genres.length > 0 && (
                    <span className="text-neutral-400">{title.genres.slice(0, 2).join(", ")}</span>
                  )}
                </div>

                <div className="flex gap-0 justify-start">
                  <Button variant="ghost-sm" className="pl-0" onClick={handlePlay}>
                    <Play size={18} />
                    <span className="ml-2 text-sm md:text-base font-semibold">Play</span>
                  </Button>
                  <Button variant="ghost-sm" className="pl-0" onClick={handleMoreInfo}>
                    <Info size={18} />
                    <span className="ml-2 text-sm md:text-base font-semibold">More Info</span>
                  </Button>
                </div>

                {/* Director - only on mobile layout */}
                {title.directorName && (
                  <p className="text-xs md:hidden text-neutral-400">
                    Directed by <span className="text-neutral-300 font-medium">{title.directorName}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
